import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { X as XIcon } from "@styled-icons/heroicons-outline/X"
import { Menu as MenuIcon } from "@styled-icons/heroicons-outline/Menu"
import { ChevronDown } from "@styled-icons/heroicons-outline/ChevronDown"
import { ChevronRight } from "@styled-icons/heroicons-outline/ChevronRight"
import { ClientOnly } from "./"
import LoginOrProfile from "../idealline/LoginOrProfile"
import FirebaseProvider from "../idealline/Firebase"
import Logo from './../../images/open-racer.inline.svg'
import { Disclosure, Menu } from "@headlessui/react"

const navigation = [
    { name: "Tracks", href: "/tracks" },
    { name: "Karting", href: "/go-karting-tracks" },
    { name: "Getting Faster", href: "/tag/getting-faster" },
]

const toolsMenu = {
    main: {
        name: "Racing Line Editor",
        href: "/racing-line-editor",
        description: "Create and analyze racing lines",
    },
    importTools: [
        {
            name: "iRacing Telemetry",
            href: "/iracing",
            description: "Analyze iRacing telemetry in 3D",
        },
        {
            name: "GoPro Importer",
            href: "/gopro-racing-line-importer",
            description: "Extract GPS data from videos",
            badge: "New"
        },
        {
            name: "Alfano Importer",
            href: "/alfano-racing-line-importer",
            description: "Import data logger files",
            badge: "New"
        },
    ]
}
  
const Navigation = () => (
    <Disclosure
        as="nav"
        className="py-5 px-6vw lg:px-0 w-full sticky h-22 top-0 z-20 bg-white/80 backdrop-blur-md"
    >
        {({ open }) => (
            <div className='max-w-screen-lg mx-auto h-full'>
                <div className="h-10 flex items-center">
                    <a href="/">
                        <Logo className="w-36" />
                    </a>
                    <div className="hidden md:block">
                        <ul className="uppercase flex space-x-5 px-5 text-sm items-center">
                            {navigation.map(({ name, href }) => (
                                <li key={name}>
                                    <a href={href} className="hover:text-[#5159a2] transition-colors">{name}</a>
                                </li>
                            ))}
                            {/* Tools Dropdown */}
                            <li>
                                <Menu as="div" className="relative inline-block text-left">
                                    <Menu.Button className="inline-flex items-center gap-1 hover:text-[#5159a2] transition-colors uppercase">
                                        Tools
                                        <ChevronDown className="w-4 h-4" />
                                    </Menu.Button>
                                    <Menu.Items className="absolute right-0 mt-3 w-80 origin-top-right bg-white rounded-xl shadow-xl border border-[#c5d2d9] focus:outline-none overflow-hidden z-50">
                                        <div className="p-4">
                                            {/* Main Tool - Racing Line Editor */}
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <a
                                                        href={toolsMenu.main.href}
                                                        className={`${
                                                            active ? 'bg-[#5159a2] border-[#5159a2]' : 'bg-white border-[#c5d2d9]'
                                                        } block px-4 py-4 rounded-xl border-2 transition-all group hover:shadow-lg hover:bg-[#5159a2]`}
                                                    >
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div className="flex-1 min-w-0">
                                                                <div className={`font-bold text-base transition-colors ${active ? 'text-white' : 'text-[rgb(34,40,49)] group-hover:text-white'}`}>
                                                                    {toolsMenu.main.name}
                                                                </div>
                                                                <div className={`text-xs mt-1.5 transition-colors ${active ? 'text-white/90' : 'text-gray-600 group-hover:text-white/90'}`}>
                                                                    {toolsMenu.main.description}
                                                                </div>
                                                            </div>
                                                            <ChevronRight className={`w-5 h-5 group-hover:translate-x-0.5 transition-all flex-shrink-0 ${active ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} />
                                                        </div>
                                                    </a>
                                                )}
                                            </Menu.Item>

                                            {/* Divider */}
                                            <div className="my-4 border-t border-[#c5d2d9]"></div>

                                            {/* Import Tools Section */}
                                            <div>
                                                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-3 px-2">
                                                    Import Tools
                                                </div>
                                                <div className="space-y-2">
                                                    {toolsMenu.importTools.map(({ name, href, description, badge }) => (
                                                        <Menu.Item key={name}>
                                                            {({ active }) => (
                                                                <a
                                                                    href={href}
                                                                    className={`${
                                                                        active ? 'bg-gray-50 border-[#c5d2d9]' : 'border-transparent'
                                                                    } block px-4 py-3 rounded-xl border hover:bg-gray-50 hover:border-[#c5d2d9] transition-all group`}
                                                                >
                                                                    <div className="flex items-start gap-3">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="font-semibold text-[rgb(34,40,49)] text-sm group-hover:text-[#5159a2] transition-colors flex items-center gap-2 flex-wrap">
                                                                                <span>{name}</span>
                                                                                {badge && (
                                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#5159a2] text-white uppercase tracking-wide">
                                                                                        {badge}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <div className="text-xs text-gray-600 mt-1">
                                                                                {description}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </a>
                                                            )}
                                                        </Menu.Item>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </Menu.Items>
                                </Menu>
                            </li>
                        </ul>
                    </div>
                    <div className="ml-auto flex items-center space-x-3">
                        <ClientOnly>
                            <FirebaseProvider>
                                <LoginOrProfile />
                            </FirebaseProvider>
                        </ClientOnly>
                        <Disclosure.Button
                            as="button"
                            className="inline-flex items-center justify-center p-1 md:hidden rounded-full hover:bg-indigo-100 "
                        >
                            <>
                                <span className="sr-only">Open main menu</span>
                                {open ? (
                                    <XIcon
                                        className="block w-6 h-6"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <MenuIcon
                                        className="block w-6 h-6"
                                        aria-hidden="true"
                                    />
                                )}
                            </>
                        </Disclosure.Button>
                    </div>
                </div>
                <Disclosure.Panel className="md:hidden pt-1 pb-2">
                    {navigation.map(({ name, href }) => (
                        <a
                            href={href}
                            key={name}
                            className="uppercase block text-indigo-800 bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-800 my-3 px-4 py-2 rounded-full disabled:opacity-50 text-sm"
                        >
                            {name}
                        </a>
                    ))}

                    {/* Tools Section for Mobile */}
                    <div className="mt-4 pt-4 border-t-2 border-[#c5d2d9]">
                        <div className="text-xs uppercase text-gray-500 px-2 mb-3 font-bold tracking-widest">
                            Tools
                        </div>

                        {/* Racing Line Editor - Featured */}
                        <a
                            href={toolsMenu.main.href}
                            className="block bg-[#5159a2] hover:bg-[#5159a2]/90 my-2 px-4 py-4 rounded-xl shadow-md hover:shadow-lg transition-all"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-white text-base">
                                        {toolsMenu.main.name}
                                    </div>
                                    <div className="text-xs text-white/90 mt-1.5">
                                        {toolsMenu.main.description}
                                    </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-white flex-shrink-0" />
                            </div>
                        </a>

                        {/* Import Tools */}
                        <div className="mt-5 mb-2">
                            <div className="text-[10px] uppercase text-gray-500 px-2 mb-3 font-bold tracking-widest">
                                Import Tools
                            </div>
                            {toolsMenu.importTools.map(({ name, href, description, badge }) => (
                                <a
                                    href={href}
                                    key={name}
                                    className="block bg-white hover:bg-gray-50 my-2 px-4 py-3.5 rounded-xl border border-[#c5d2d9] hover:border-[#5159a2] transition-all"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-[rgb(34,40,49)] text-sm flex items-center gap-2 flex-wrap">
                                                <span>{name}</span>
                                                {badge && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#5159a2] text-white uppercase tracking-wide">
                                                        {badge}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-600 mt-1">
                                                {description}
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </Disclosure.Panel>
            </div>
        )}
    </Disclosure>
)
export default Navigation
